import BookingTimeout from '@/components/App/BookingTimeout';
import BookingHeader from '@/components/App/Header/BookingHeader';
import HotelJapaneseName from '@/components/App/Hotels/HotelJapaneseName';
import HotelStar from '@/components/App/Hotels/HotelStar';
import SingleOrPluralText from '@/components/App/SingleOrPluralText';
import history from '@/history';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { apiSdk } from '@/httpClient';
import { AppUrls, generateHotelDetailUrlWithFullInfo } from '@/urls';
import { faChevronLeft, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sumBy } from 'lodash';
import moment from 'moment';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Modal, ModalBody, ModalFooter, PopoverBody, PopoverHeader, UncontrolledPopover, UncontrolledTooltip } from 'reactstrap';
import DefaultCancellationPolicy from '../HotelDetail/components/DefaultCancellationPolicy';
import PlanTaxAndFee from '../HotelDetail/components/PlanTaxAndFee';
import { ModalUtils } from '@/contexts/ModalWrapper';
import ModalIconWithStatus from '@/components/App/ModalIconWithStatus';
import Spin from '@/components/Spin';
import { PrebookHotelConfirmationModel } from '@/__generated';
import PageLoading from '@/components/App/PageLoading';
import { HOTEL_CONFIG, appDisplayDateFormat } from '@/config';
import LabelValueItem from '@/components/App/BookingComponents/LabelValueItem';
import { handleBookingNotFound } from '@/helpers/errorHandle';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { Helmet } from 'react-helmet';
import RoomPlanMealDetail from '@/components/App/RoomPlanMealDetail';

type Props = {};

var valueClassName = 'fw-500 py-2';
export default function BookingConfirmationPage({}: Props) {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { formatCurrency } = useCurrencyFormat();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [displayResearchModal, setDisplayResearchModal] = React.useState<boolean>(false);
  const [bookingSummary, setBookingSummary] = React.useState<PrebookHotelConfirmationModel>();

  const history = useHistory();
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }

  const getHotelBookingSummary = (sId: string) => {
    setLoading(true);
    apiSdk.BookingService.getPrebookConfirmationInfo({ sessionId: sId })
      .then(res => {
        if (!res.sessionExpired && res.selectedHotel && (res.ttl || 0) > 0) {
          setBookingSummary(res);
        } else {
          if (hasDataForResearch(bookingSummary)) {
            setDisplayResearchModal(true);
          } else {
            handleBookingNotFound();
          }
        }
      })
      .catch(() => {
        history.push(AppUrls.HomePage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (sessionId) {
      getHotelBookingSummary(sessionId);
    } else {
      history.push(AppUrls.HomePage);
    }
  }, [sessionId]);

  const numberOfNight = React.useMemo(() => {
    return moment(bookingSummary?.checkoutDate).diff(moment(bookingSummary?.checkinDate), 'day');
  }, [bookingSummary]);

  const hasDataForResearch = (preBookData: PrebookHotelConfirmationModel | undefined | null) => {
    if (preBookData) {
      return true;
    }
    return false;
  };

  const onTimeout = () => {
    if (hasDataForResearch(bookingSummary)) {
      setDisplayResearchModal(true);
    } else {
      handleBookingNotFound();
    }
  };

  const hotelDetailUrl = React.useMemo(() => {
    if (bookingSummary && bookingSummary.selectedHotel) {
      return generateHotelDetailUrlWithFullInfo(
        bookingSummary.selectedHotel?.hotelName || '',
        bookingSummary.selectedHotel?.hotelCode,
        bookingSummary.checkinDate,
        bookingSummary.checkoutDate,
        bookingSummary.boardType,
        bookingSummary.rooms || [],
      );
    }
    return null;
  }, [bookingSummary]);

  const try2ResearchBooking = () => {
    setLoading(true);
    apiSdk.BookingService.prebookHotel({
      body: {
        hotelCode: bookingSummary?.selectedHotel?.hotelCode || '',
        sessionId: sessionId,
        rooms: bookingSummary?.selectedHotel?.rooms?.map(room => ({
          noOfAdult: room.noOfAdult,
          noOfChild: room.noOfChild,
          noOfRooms: room.noOfRooms,
          roomPlanId: room.planId,
        })),
      },
    }).then(res => {
      if (res.sessionId) {
        setDisplayResearchModal(false);
        getHotelBookingSummary(res.sessionId);
      } else {
        setDisplayResearchModal(false);
        ModalUtils.error({
          title: 'Error!',
          content: (
            <span>
              We apologize for the inconvenience.
              <br /> The site is currently busy. Please try again later!
            </span>
          ),
          onClose: () => history.push(AppUrls.HomePage),
        });
      }
    });
  };
  const mainImage = React.useMemo(() => {
    let images = bookingSummary?.selectedHotel?.images || [];
    if (images?.length) {
      return images[0];
    }
    return HOTEL_CONFIG.defaultImage;
  }, [bookingSummary]);
  if (loading) {
    return <PageLoading />;
  }

  return bookingSummary ? (
    <div className="mb-50">
      <Helmet>
        <title>{`Booking confirmation - ${bookingSummary.sessionId}`}</title>
      </Helmet>
      {displayResearchModal ? (
        <Modal className={'error static-modal'} isOpen={true} unmountOnClose={true}>
          <Spin loading={loading}>
            <ModalBody>
              <ModalIconWithStatus status="error" />
              <span className="title">Booking session timeout!</span>
              <br />

              <span className="content">Your session is expired, you can try create new session or go to hotel detail page</span>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
              <Link to={hotelDetailUrl || ''} className="button text-blue-1">
                <FontAwesomeIcon icon={faChevronLeft} className="mr-5 text-12" />
                Go to hotel detail
              </Link>
              <button className="button px-15 bg-red-1  py-10 text-white" onClick={try2ResearchBooking}>
                New booking
                <i className="icon-arrow-top-right text-12 px-5" />
              </button>
            </ModalFooter>
          </Spin>
        </Modal>
      ) : null}
      <BookingHeader isSticky />
      <div className="header-margin" />
      <BookingTimeout ttl={bookingSummary.ttl || 0} onTimeout={onTimeout} />
      <div className="container mt-20">
        <div className="row x-gap-10 y-gap-10">
          <div className="col-xl-8 col-lg-8">
            <div className="row x-gap-10 y-gap-10">
              <div className="col-md-3 col-xs-12">
                <img className="rounded w-100" src={mainImage} />
              </div>
              <div className="col-md-9 col-xs-12">
                <div className="row ">
                  <div className="col">
                    <h1 className="text-25  fw-500">
                      {bookingSummary?.selectedHotel?.hotelName}
                      <HotelJapaneseName name={bookingSummary.selectedHotel?.hotelNameJp} addBefore="-" />
                    </h1>
                  </div>
                </div>
                {bookingSummary.selectedHotel?.starRating ? (
                  <div className="mt-5">
                    <HotelStar star={bookingSummary.selectedHotel?.starRating || 0} />
                  </div>
                ) : null}

                {bookingSummary.selectedHotel?.access ? (
                  <div className="mt-5 d-flex items-center">
                    <i className="icon-award text-20 mr-5 text-blue-1" />
                    {bookingSummary.selectedHotel?.access}
                  </div>
                ) : null}

                <div className="mt-5 d-flex items-center">
                  <i className="icon-location mr-5 text-20 text-blue-1" />
                  {bookingSummary.selectedHotel?.address}
                </div>
              </div>
            </div>
            <div className="col-12  pt-20 pb-10">
              <div className="d-flex align-items-center">
                <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
                <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase px-10">Room detail</div>
                <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              </div>
            </div>
            <div className="col-12">
              <div className="p-2">
                {bookingSummary.selectedHotel?.rooms?.map((room, index) => {
                  return (
                    <div className="row x-gap-10 y-gap-10 border-light rounded mb-20" key={index}>
                      <div className="col-12 fw-500 text-18 px-10 py-5 bg-green-2 text-white mb-10">{`Room #${index + 1}`}</div>
                      <div className="row x-gap-10 y-gap-10 p-2">
                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem label="Room Category" value={room?.roomTypeCategory} valueClassName={valueClassName} />
                        </div>

                        <div className="col-md-8 col-xs-12">
                          <LabelValueItem label="Room Type Name" value={room?.roomTypeName} valueClassName={valueClassName} />
                        </div>

                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem
                            label="Meal Type"
                            value={
                              <div className="d-flex">
                                {[room.boardTypeCode, room?.boardType].filter(x => x).join(' ')}
                                <RoomPlanMealDetail planDetail={room} supplier={room.supplier} />
                              </div>
                            }
                            valueClassName={valueClassName}
                          />
                        </div>
                        <div className="col-md-8 col-xs-12">
                          <LabelValueItem
                            label="Plan"
                            value={[room?.planCode, room.planName].filter(x => x).join(' ')}
                            valueClassName={valueClassName}
                          />
                        </div>
                        {room.planTitle ? (
                          <div className="col-md-12 col-xs-12">
                            <LabelValueItem
                              label="Plan Detail"
                              value={
                                <div className="d-flex">
                                  {room.planTitle}
                                  {room.planDesc ? (
                                    <div>
                                      <FontAwesomeIcon
                                        id={`room-plan-title-${index}`}
                                        icon={faCircleInfo}
                                        className="text-blue-1 ml-5"
                                        style={{ cursor: 'pointer' }}
                                      />

                                      <UncontrolledTooltip target={`#room-plan-title-${index}`}>
                                        <div className="text-14">{room.planDesc}</div>
                                      </UncontrolledTooltip>
                                    </div>
                                  ) : null}
                                </div>
                              }
                              valueClassName={valueClassName}
                            />
                          </div>
                        ) : null}
                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem
                            label="Guests"
                            value={`${room.noOfAdult} adult(s) ${room.noOfChild} child`}
                            valueClassName={valueClassName}
                          />
                        </div>
                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem
                            label="Check-in Time"
                            value={room.checkinTime || bookingSummary.selectedHotel?.checkinTime}
                            valueClassName={valueClassName}
                          />
                        </div>
                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem
                            label="Check-out Time"
                            value={room.checkoutTime || bookingSummary.selectedHotel?.checkoutTime}
                            valueClassName={valueClassName}
                          />
                        </div>
                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem
                            label="No. of Rooms"
                            valueClassName={valueClassName}
                            value={<SingleOrPluralText count={room.noOfRooms || 0} singleText="{} room" pluralText="{} rooms" />}
                          />
                        </div>
                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem label="Taxes & Fees" value={<PlanTaxAndFee plan={room as any} />} valueClassName={valueClassName} />
                        </div>

                        <div className="col-md-4 col-xs-12">
                          <LabelValueItem label="Price" value={formatCurrency(room.price)} valueClassName={valueClassName} />
                        </div>

                        <div className="col-md-12 col-xs-12">
                          <LabelValueItem
                            label="Cancellation Policy"
                            value={<DefaultCancellationPolicy fontSize={16} checkinDate={bookingSummary.checkinDate} plan={room} />}
                            valueClassName={valueClassName}
                          />
                        </div>
                        {room.supplier === 'NTA' ? (
                          <div className="col-12">
                            <LabelValueItem
                              label="Payment Notes"
                              value={
                                room.remarks ? (
                                  <>
                                    <div className="text-red-1">Infant Remarks: {room.remarks}</div>
                                    {room.paymentNotes}
                                  </>
                                ) : (
                                  room.paymentNotes
                                )
                              }
                              valueClassName={valueClassName}
                            />
                          </div>
                        ) : (
                          <div className="col-12">
                            <LabelValueItem
                              label="Payment Notes"
                              value={
                                <>
                                  <div className="text-red-1">{room.remarks}</div>
                                  {room.paymentNotes}
                                </>
                              }
                              valueClassName={valueClassName}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-4 ">
            <div className="px-30 py-30 border-light rounded-4">
              <div className="text-20 fw-500 mb-10">Price summary</div>
              <div className="border-top-light mt-10 mb-20" />
              <div className="row y-gap-20 justify-between">
                <div className="col-auto">
                  <div className="text-15">Check-in</div>
                  <div className="fw-500">{appDisplayDateFormat.default.dateOnly(bookingSummary.checkinDate)}</div>
                  {bookingSummary.selectedHotel?.checkinTime ? (
                    <div className="text-15 text-light-1">{bookingSummary.selectedHotel?.checkinTime}</div>
                  ) : null}
                </div>
                <div className="col-auto md:d-none">
                  <div className="h-full w-1 bg-border" />
                </div>
                <div className="col-auto text-right md:text-left">
                  <div className="text-15">Check-out</div>
                  <div className="fw-500">{appDisplayDateFormat.default.dateOnly(bookingSummary.checkoutDate)}</div>
                  {bookingSummary.selectedHotel?.checkoutTime ? (
                    <div className="text-15 text-light-1">{bookingSummary.selectedHotel?.checkoutTime}</div>
                  ) : null}
                </div>
              </div>
              {/* End row */}
              <div className="border-top-light mt-10 mb-10" />
              <div>
                <div className="text-15">Number of rooms:</div>
                <div className="fw-500">
                  <SingleOrPluralText count={bookingSummary.rooms?.length || 0} singleText="{} room" pluralText="{} rooms" />
                </div>
              </div>
              <div className="border-top-light mt-10 mb-10" />
              <div>
                <div className="text-15">Total length of stay:</div>
                <div className="fw-500">
                  <SingleOrPluralText count={numberOfNight} singleText="{} night" pluralText="{} nights" />
                </div>
              </div>
              <div className="border-top-light mt-10 mb-10" />
              <div>
                <div className="text-15">Total Guests:</div>
                <div className="fw-500">
                  <SingleOrPluralText
                    count={sumBy(bookingSummary.rooms, x => (x.noOfAdult || 0) + (x.noOfChild || 0))}
                    singleText="{} guest"
                    pluralText="{} guests"
                  />
                </div>
              </div>
              <div className="border-top-light mt-10 mb-10" />

              <div className="text-15">Total Price</div>
              <div className="text-center">
                <span className="fw-500 text-22 text-blue-1">{formatCurrency(bookingSummary.selectedHotel?.totalPrice)}</span>
              </div>
              <Link
                to={AppUrls.__createBookingPassengerUrl(bookingSummary.sessionId || '')}
                className="button h-50 w-100 -dark-1 bg-blue-1 text-white text-18 mt-10"
              >
                Next
                <i className="icon-arrow-top-right ml-15" />
              </Link>
              {hotelDetailUrl ? (
                <div className="mt-10">
                  <Link to={hotelDetailUrl} className="text-red-1 fw-500 text-14 underline d-flex items-center">
                    <FontAwesomeIcon icon={faChevronLeft} className="mr-5" />
                    Select other rooms?
                  </Link>
                </div>
              ) : null}
              {/* End row */}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
